import { useCallback, useEffect, useState } from "react"
import type { OpenCodeApi } from "../../lib/opencodeApi"
import type { FileNode } from "../../lib/opencodeTypes"
import { Button, Empty, Spinner } from "../ui"

interface ArtifactsBrowserProps {
  api: OpenCodeApi
}

// Extensions safe to render as text. Anything else — xlsx, pptx, unknown
// binaries — is download-only: guessing a mime from the response headers is
// not enough, because the container serves whatever it sniffed and an octet
// stream forced into a <pre> is mojibake at best.
const TEXT_EXTENSIONS = [".md", ".csv", ".json", ".txt", ".log", ".yaml", ".yml", ".xml", ".ts", ".tsx", ".js", ".py", ".sql"]

function isTextArtifact(name: string): boolean {
  const dot = name.lastIndexOf(".")
  if (dot < 0) return false
  return TEXT_EXTENSIONS.includes(name.slice(dot).toLowerCase())
}

function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = name
  anchor.click()
  URL.revokeObjectURL(url)
}

// Deliverables the agent wrote to /workspace/artifacts. A flat list, not a
// tree: the convention is one folder of finished files, and the container's
// fs.list offers no size or modified time anyway — names are the whole row.
export function ArtifactsBrowser({ api }: ArtifactsBrowserProps) {
  const [artifacts, setArtifacts] = useState<FileNode[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState<string | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const refresh = useCallback(() => {
    setBusy(true)
    api
      .listArtifacts()
      .then((entries) => {
        setArtifacts(entries)
        setError(null)
      })
      .catch((err) => {
        console.error("Failed to load artifacts:", err)
        setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        setBusy(false)
        setLoading(false)
      })
  }, [api])

  useEffect(() => {
    refresh()
  }, [refresh])

  const openArtifact = useCallback(
    async (path: string) => {
      setSelectedFile(path)
      setFileContent(null)
      setFileError(null)
      try {
        const blob = await api.readArtifact(path)
        setFileContent(await blob.text())
      } catch (err) {
        console.error("Failed to load artifact:", err)
        setFileError(err instanceof Error ? err.message : String(err))
      }
    },
    [api],
  )

  const download = useCallback(
    async (artifact: FileNode) => {
      try {
        const blob = await api.readArtifact(artifact.path)
        downloadBlob(blob, artifact.name)
      } catch (err) {
        console.error("Failed to download artifact:", err)
        setError(err instanceof Error ? err.message : String(err))
      }
    },
    [api],
  )

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const selectedIsText = selectedFile !== null && isTextArtifact(selectedFile)
  const selectedArtifact = artifacts.find((artifact) => artifact.path === selectedFile) ?? null

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border p-3">
        <h3 className="text-sm font-semibold text-foreground">Artifacts</h3>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="rounded-md px-2 py-1 text-xs text-muted transition-colors hover:bg-surface-muted hover:text-foreground disabled:opacity-50"
        >
          {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && (
        <p className="border-b border-border px-3 py-2 text-xs text-danger">{error}</p>
      )}

      <div className="flex flex-1 overflow-hidden">
        <div className="flex w-64 shrink-0 flex-col overflow-y-auto border-r border-border">
          {artifacts.length === 0 ? (
            <p className="p-3 text-xs text-muted">No artifacts yet.</p>
          ) : (
            artifacts.map((artifact) => (
              <div
                key={artifact.path}
                className={`flex items-center gap-1 px-2 py-1 text-xs ${
                  selectedFile === artifact.path ? "bg-accent-soft text-accent" : "text-foreground"
                }`}
              >
                <button
                  type="button"
                  onClick={() => void openArtifact(artifact.path)}
                  disabled={artifact.type === "directory" || !isTextArtifact(artifact.name)}
                  className="min-w-0 flex-1 truncate py-1 text-left hover:underline disabled:no-underline disabled:opacity-70"
                  title={isTextArtifact(artifact.name) ? artifact.name : `${artifact.name} (download only)`}
                >
                  📄 {artifact.name}
                </button>
                {artifact.type !== "directory" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void download(artifact)}
                    title={`Download ${artifact.name}`}
                  >
                    Download
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        <div className="flex-1 overflow-auto">
          {selectedFile ? (
            <div className="p-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-xs text-muted">{selectedFile}</span>
                <Button size="sm" onClick={() => selectedArtifact && void download(selectedArtifact)}>
                  Download
                </Button>
              </div>
              {fileError ? (
                <p className="text-xs text-danger">{fileError}</p>
              ) : selectedIsText ? (
                <pre className="overflow-auto rounded bg-surface-muted p-3 text-xs">
                  <code>{fileContent ?? "Loading…"}</code>
                </pre>
              ) : (
                <Empty
                  title="Not previewable"
                  hint="Binary artifacts are download only — use the Download button above."
                />
              )}
            </div>
          ) : (
            <Empty
              title="No artifact selected"
              hint="Ask the agent to create an artifact, then Refresh and pick one here"
            />
          )}
        </div>
      </div>
    </div>
  )
}
